import { createClient } from '@supabase/supabase-js';
import { logger } from './logger';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Define public routes that don't require authentication
const PUBLIC_ROUTES = ['/login', '/signup', '/', '/features', '/about', '/terms', '/privacy', '/pricing'];

// Cache for responses to avoid repeated network requests
const responseCache = new Map<string, {
  data: any;
  timestamp: number;
  expiresIn: number;
}>();

export const CACHE_TTL = {
  SHORT: 5 * 1000,      // 5 seconds
  MEDIUM: 15 * 1000,    // 15 seconds
  LONG: 2 * 60 * 1000   // 2 minutes
};

// Validate URL format to catch obvious issues early
if (supabaseUrl && supabaseAnonKey) {
  try {
    new URL(supabaseUrl);
  } catch (e) {
    logger.error('Invalid Supabase URL format:', e);
  }
} else {
  logger.error('Missing Supabase environment variables.');
}

// Cache key generator for requests
const getCacheKey = (resource: RequestInfo, config: RequestInit = {}): string => {
  const url = typeof resource === 'string' ? resource : resource.url;
  const method = config.method || 'GET';
  
  // Only cache GET requests
  if (method !== 'GET') return '';
  
  // Don't cache auth-related requests
  if (url.includes('/auth/')) return '';
  
  // Generate a cache key from the URL and any query params
  return `${method}:${url}`;
};

// Function to determine cache TTL based on the endpoint
const getCacheTTL = (url: string): number => {
  // User data needs to be fairly fresh
  if (url.includes('/profiles')) return CACHE_TTL.SHORT;
  
  // Analysis data can be cached a bit longer
  if (url.includes('/content_analyses')) return CACHE_TTL.MEDIUM;
  
  // Plan and subscription data rarely changes
  if (url.includes('/user_plans') || url.includes('/user_subscriptions') || url.includes('/stripe_user_subscriptions')) return CACHE_TTL.SHORT;
  
  // Default to short cache time
  return CACHE_TTL.SHORT;
};

// Check and get cached response
const getCachedResponse = (resource: RequestInfo, config: RequestInit = {}): Response | null => {
  const cacheKey = getCacheKey(resource, config);
  if (!cacheKey) return null;
  
  const cached = responseCache.get(cacheKey);
  if (!cached) return null;
  
  const now = Date.now();
  if (now - cached.timestamp > cached.expiresIn) {
    // Cache expired
    responseCache.delete(cacheKey);
    return null;
  }
  
  return new Response(JSON.stringify(cached.data), {
    status: 200,
    headers: { 'Content-Type': 'application/json' }
  });
};

// Store response in cache
const cacheResponse = async (resource: RequestInfo, config: RequestInit = {}, response: Response): Promise<void> => {
  const cacheKey = getCacheKey(resource, config);
  if (!cacheKey) return;
  
  try {
    const url = typeof resource === 'string' ? resource : resource.url;
    const clonedResponse = response.clone();
    const data = await clonedResponse.json();
    
    responseCache.set(cacheKey, {
      data,
      timestamp: Date.now(),
      expiresIn: getCacheTTL(url)
    });

    // Limit cache size to prevent memory leaks
    if (responseCache.size > 100) {
      // Remove oldest entries
      const entries = Array.from(responseCache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      const toRemove = entries.slice(0, entries.length - 50);
      toRemove.forEach(([key]) => responseCache.delete(key));
    }
  } catch (error) {
    logger.error('Error caching response:', error);
  }
};

// Enhanced fetch with retry logic and better error handling
const enhancedFetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
  const [resource, config = {}] = args;
  const maxRetries = 3; // Increased from 2 to 3 for better reliability

  // Check if we're on a public route
  const isPublicRoute = PUBLIC_ROUTES.some(route => 
    window.location.pathname === route || 
    window.location.pathname.startsWith(route + '/')
  );
  
  // Check if this is a logout request and use longer timeout
  const isLogoutRequest = typeof resource === 'string' && resource.includes('/auth/v1/logout');
  const isAuthRequest = typeof resource === 'string' && resource.includes('/auth/v1/');
  const isTokenRefreshRequest = typeof resource === 'string' && 
    resource.includes('/auth/v1/token') && 
    resource.includes('grant_type=refresh_token');
  
  // Determine if this is a storage upload request
  const isStorageUpload = typeof resource === 'string' && 
    resource.includes('/storage/v1/object') && 
    (config.method === 'POST' || config.method === 'PUT');
  
  // Set appropriate timeouts based on request type
  const maxTimeout = isStorageUpload ? 300000 : // 5 minutes for storage uploads
                    isLogoutRequest ? 8000 : 
                    isAuthRequest ? 10000 : 
                    8000;
  
  let lastError: Error | null = null;
  let attempt = 0;

  // For debugging: Log the request
  const requestUrl = typeof resource === 'string' ? resource : resource.url;
  logger.debug(`[Fetch] Requesting ${requestUrl}, timeout: ${maxTimeout}ms, retry: ${maxRetries}`);

  // Skip token refresh requests on public routes
  if (isTokenRefreshRequest && isPublicRoute) {
    logger.debug(`[Fetch] Skipping token refresh on public route: ${window.location.pathname}`);
    return new Response(JSON.stringify({ 
      error: 'skipped_on_public_route',
      message: 'Token refresh skipped on public route'
    }), {
      status: 200,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  // Check cache first for GET requests
  const cachedResponse = getCachedResponse(resource, config);
  if (cachedResponse) {
    logger.debug(`[Fetch] Using cached response for ${requestUrl}`);
    return cachedResponse;
  }

  // Try to fetch with retries
  while (attempt < maxRetries) {
    attempt++;
    
    try {
      logger.debug(`[Fetch] Attempt ${attempt}/${maxRetries} for ${requestUrl}`);
      
      // Create timeout promise
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error(`Request to ${requestUrl} timed out after ${maxTimeout}ms`));
        }, maxTimeout);
      });
      
      // Execute the fetch with timeout
      const response: Response = await Promise.race([fetch(resource, config), timeoutPromise]);
      
      // Error handling for common HTTP error status codes
      if (!response.ok) {
        const status = response.status;
        let errorText = '';
        let errorData = null;
        
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            try {
              errorData = await response.clone().json();
              errorText = JSON.stringify(errorData);
            } catch (parseErr) {
              errorText = await response.clone().text() || 'Could not parse JSON error response';
            }
          } else {
            errorText = await response.clone().text() || 'No error details available';
          }
        } catch (e) {
          errorText = 'Could not parse error response';
        }
        
        logger.error(`Supabase request failed with status ${status}:`, {
          url: requestUrl,
          status,
          body: errorText,
          data: errorData
        });

        // Skip refresh token errors on public routes
        if (isPublicRoute && 
            (errorText.includes('refresh_token_not_found') || 
             (errorData?.code === 'refresh_token_not_found'))) {
          logger.debug('Ignoring refresh token error on public route');
          return new Response(JSON.stringify({ 
            error: 'refresh_token_not_found',
            message: 'Refresh token not found, but on public route'
          }), {
            status: 200,
            headers: { 'Content-Type': 'application/json' }
          });
        }

        // Check for refresh token errors first
        if (errorText.includes('refresh_token_not_found') || 
            (errorData?.code === 'refresh_token_not_found')) {
          logger.error('Refresh token not found error detected');
          // Clear auth cache and throw specific error
          clearAuthCache();
          const error = new Error('refresh_token_not_found');
          (error as any).statusCode = status;
          (error as any).details = errorText;
          throw error;
        }

        // Handle other auth errors (401/403)
        if (status === 401 || status === 403) {
          const error = new Error(`Authentication error: ${status}`);
          (error as any).statusCode = status;
          (error as any).details = errorText;
          throw error;
        }
        
        // For 400 (bad request) specific to RLS issues with profiles table
        if (status === 400) {
          // Don't retry bad requests
          const error = new Error(`Bad request: ${errorText}`);
          (error as any).statusCode = status;
          (error as any).details = errorText;
          
          if (requestUrl.toString().includes('profiles')) {
            logger.error('Possible RLS policy issue with profiles table');
          }
          
          throw error;
        }
        
        // For other errors, we should retry
        if (attempt < maxRetries) {
          const delay = Math.min(2 ** (attempt - 1) * 700, 3000);
          logger.warn(`HTTP error ${status}, retrying in ${delay}ms`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        
        // If this is our last attempt, return the error response
        return response;
      }
      
      logger.debug(`[Fetch] Successfully completed request to ${requestUrl}`);
      
      // Cache the successful response for future use
      if (config.method !== 'POST' && config.method !== 'PUT' && config.method !== 'DELETE') {
        await cacheResponse(resource, config, response.clone());
      }
      
      return response;
    } catch (err) {
      // Log the actual error for debugging
      logger.error(`[Fetch] Error during attempt ${attempt}:`, err);
      
      // If this is a refresh token error, throw it immediately
      if (err instanceof Error && err.message === 'refresh_token_not_found') {
        throw err;
      }
      
      lastError = err instanceof Error ? err : new Error(String(err));
      
      // For network errors or timeouts, retry with exponential backoff
      if (err instanceof TypeError || 
          (err instanceof Error && err.message?.includes('timed out')) || 
          (err instanceof Error && err.message?.includes('fetch'))) {
        // Use more aggressive backoff for storage uploads
        const baseDelay = isStorageUpload ? 1500 : 700;
        const maxDelay = isStorageUpload ? 10000 : 3000;
        const delay = Math.min(2 ** (attempt - 1) * baseDelay, maxDelay);
        
        logger.warn(`Fetch attempt ${attempt} failed, retrying in ${delay}ms:`, err.message);
        
        // Don't retry if we've reached max attempts
        if (attempt >= maxRetries) {
          break;
        }
        
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      // For other errors, don't retry
      throw err;
    }
  }
  
  // If we got here and it's not a Supabase request, we need to throw the error
  if (lastError) {
    throw lastError;
  }
  
  // Fallback error if somehow we get here without an error
  throw new Error('Max retries exceeded');
};

export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '', {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: localStorage,
    storageKey: 'supabase.auth.token',
  },
  // Add global error handler for network issues
  global: {
    fetch: enhancedFetch
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// Helper function to validate Supabase connection
export const testSupabaseConnection = async (): Promise<boolean> => {
  try {
    // Simple query to check if we can connect to Supabase
    const { error } = await supabase
      .from('profiles')
      .select('id')
      .limit(1);
      
    if (error) {
      logger.error('Supabase connection test failed:', error);
      return false;
    }
    
    logger.debug('Supabase connection successful');
    return true;
  } catch (err) {
    logger.error('Failed to connect to Supabase:', err);
    
    return false;
  }
};

// Function to clear the response cache
export const clearResponseCache = (pattern?: string): void => {
  if (pattern) {
    // Clear only keys matching the pattern
    for (const key of responseCache.keys()) {
      if (key.includes(pattern)) {
        responseCache.delete(key);
      }
    }
    logger.debug(`Response cache cleared for pattern: ${pattern}`);
  } else {
    // Clear all cache
    responseCache.clear();
    logger.debug('Response cache cleared');
  }
};

// Clear auth-related cache specifically
export const clearAuthCache = (): void => {
  clearResponseCache('/auth/v1/');
  clearResponseCache('/user_subscriptions');
  clearResponseCache('/profiles');
  logger.debug('Auth cache cleared');
};

// Function to clear all caches
export const clearAllCaches = (): void => {
  responseCache.clear();
  
  // Clear any auth-related localStorage items
  localStorage.removeItem('supabase.auth.token');
  localStorage.removeItem('supabase.auth.expires_at');
  localStorage.removeItem('supabase.auth.refresh_token');
  localStorage.removeItem('subscriptionCache');
  localStorage.removeItem('current_subscription_id');
  localStorage.removeItem('subscription_end_date');
  
  logger.debug('All caches cleared');
};

// Function to delete all videos for a user
export const deleteUserVideos = async (userId: string): Promise<{ success: boolean; message: string }> => {
  try {
    logger.debug(`Calling delete-user-videos function for user: ${userId}`);
    
    // Add retry logic for better reliability
    const maxRetries = 3;
    let retryCount = 0;
    let lastError: any = null;
    
    while (retryCount <= maxRetries) {
      try {
        // Call the delete-user-videos Edge Function
        const response = await fetch(`${supabaseUrl}/functions/v1/delete-user-videos`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token || supabaseAnonKey}`
          },
          body: JSON.stringify({ user_id: userId })
        });
        
        if (!response.ok) {
          let errorData;
          try {
            errorData = await response.json();
          } catch (e) {
            errorData = { error: 'Failed to parse error response' };
          }
          logger.error('Error deleting user videos:', errorData);
          return { 
            success: false, 
            message: errorData.error || 'Failed to delete user videos' 
          };
        }
        
        let result;
        try {
          result = await response.json();
        } catch (e) {
          result = { message: 'Videos deleted but response could not be parsed' };
        }
        logger.debug('Videos deleted successfully:', result);
        
        return { 
          success: true, 
          message: result.message || 'Videos deleted successfully' 
        };
      } catch (error) {
        logger.error('Error deleting user videos:', error);
        return { 
          success: false, 
          message: 'Failed to delete videos. Please try again later.' 
        };
      }
    }
    throw new Error('Max retries exceeded');
  } catch (error) {
    logger.error('Error in deleteUserVideos:', error);
    return {
      success: false,
      message: 'An error occurred while deleting videos. Please try again later.'
    };
  }
};

// Function to fix user subscription issues by calling the server function
export const fixUserSubscription = async (): Promise<any> => {
  try {
    logger.debug('Calling admin_fix_user_subscription...');
    const { data, error } = await supabase.rpc('admin_fix_user_subscription');

    if (error) {
      logger.error('Error fixing user subscription:', error);
      throw error;
    }

    logger.debug('User subscription fixed:', data);
    // Clear response cache to ensure fresh data
    clearResponseCache();
    return data;
  } catch (err) {
    logger.error('Failed to fix user subscription:', err);
    throw err;
  }
};

// Function to sync all subscription IDs
export const syncAllSubscriptionIds = async (): Promise<any> => {
  try {
    logger.debug('Calling sync_all_subscription_ids...');
    const { data, error } = await supabase.rpc('sync_all_subscription_ids');

    if (error) {
      logger.error('Error syncing all subscription IDs:', error);
      throw error;
    }

    logger.debug('All subscription IDs synced:', data);
    clearResponseCache('/user_subscriptions');
    return data;
  } catch (err) {
    logger.error('Failed to sync all subscription IDs:', err);
    throw err;
  }
};

// Function to refresh all user data
export const refreshAllUserData = async (): Promise<any> => {
  try {
    logger.debug('Calling admin_refresh_all_user_data...');
    const { data, error } = await supabase.rpc('admin_refresh_all_user_data');

    if (error) {
      logger.error('Error refreshing all user data:', error);
      throw error;
    }

    logger.debug('All user data refreshed:', data);
    clearResponseCache('/user_subscriptions');
    clearResponseCache('/profiles');
    clearResponseCache('/user_plans');

    return data;
  } catch (err) {
    logger.error('Failed to refresh all user data:', err);
    throw err;
  }
};

// Function to find users with missing subscription IDs
export const findMissingSubscriptionIds = async (): Promise<any> => {
  try {
    logger.debug('Calling admin_find_missing_subscription_ids...');
    const { data, error } = await supabase.rpc('admin_find_missing_subscription_ids');

    if (error) {
      logger.error('Error finding missing subscription IDs:', error);
      throw error;
    }

    logger.debug('Found users with missing subscription IDs:', data);
    return data;
  } catch (err) {
    logger.error('Failed to find missing subscription IDs:', err);
    throw err;
  }
};

// Function to search for a user by email or ID
export const searchUserByEmailOrId = async (query: string): Promise<any> => {
  try {
    logger.debug(`Searching for user with query: ${query}`);

    const response = await fetch(`${supabaseUrl}/functions/v1/admin-search-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token || supabaseAnonKey}`
      },
      body: JSON.stringify({ query: query.trim() })
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { error: 'Failed to parse error response' };
      }
      logger.error('Error searching for user:', errorData);
      return {
        success: false,
        message: errorData.error || 'Failed to search for user'
      };
    }

    const result = await response.json();
    return result;
  } catch (err) {
    logger.error('Error searching for user:', err);
    return {
      success: false,
      message: err instanceof Error ? err.message : 'Failed to search for user'
    };
  }
};

// Function to change a user's plan
export const adminChangeUserPlan = async (targetUserId: string, newPriceId: string): Promise<any> => {
  try {
    logger.debug(`Changing plan for user ${targetUserId} to ${newPriceId}`);

    const { data, error } = await supabase.rpc('admin_change_user_plan', {
      p_target_user_id: targetUserId,
      p_new_price_id: newPriceId
    });

    if (error) {
      logger.error('Error changing user plan:', error);
      throw error;
    }

    logger.debug('User plan changed successfully:', data);
    clearResponseCache('/user_subscriptions');
    clearResponseCache('/profiles');
    clearResponseCache('/user_plans');

    return data;
  } catch (err) {
    logger.error('Failed to change user plan:', err);
    throw err;
  }
};